// useUpdateTicket hook

import { useCallback, useState } from "react";

import { useUserContext } from "../../use-auth-context";
import { useMultiSiteContext } from "../../use-site-context";
import { useRefreshTokenMutation } from "../../use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { WpGenericPost, WpTicketMetaBox } from "../../use-get/tickets/types";
import { sanitizeObjectData } from "../../use-get/utils";
import { useScreenAlert } from "@hooks/use-screen-alert";

export const useUpdateTicket = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const { setError } = useScreenAlert();

    const { user } = useUserContext();
    const { getApiUrl } = useMultiSiteContext();
    const { refreshUserToken } = useRefreshTokenMutation();

    const updateTicket = useCallback(
        async (
            id: string | number,
            input: Partial<WpGenericPost<WpTicketMetaBox>>
        ) => {
            setLoading(true);

            // Sanitize input data to prevent XSS and injection
            const sanitizedInput = sanitizeObjectData(input);

            try {
                const url = getApiUrl(`wp-json/wp/v2/ticket/${id}`);
                const response = await fetchWithTokenRefresh(
                    url,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${user?.authToken ?? ""}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(sanitizedInput),
                    },
                    () => refreshUserToken(user?.refreshToken ?? "")
                );

                if (!response.ok) {
                    const body = await response.json().catch(() => null);
                    const serverMessage: string | undefined = body?.message;
                    if (response.status === 409) {
                        setError(serverMessage ?? "This value is already in use. Please check the highlighted fields.");
                    } else {
                        setError(serverMessage ?? "Failed to save ticket. Please try again.");
                        console.error("[useUpdateTicket] HTTP", response.status, serverMessage ?? "");
                    }
                    setLoading(false);
                    return undefined;
                }

                setLoading(false);
                return await response.json();
            } catch (err) {
                setError("Failed to save ticket. Please try again.");
                console.error("[useUpdateTicket]", err);
                setLoading(false);
                return undefined;
            }
        },
        [getApiUrl, user?.authToken, user?.refreshToken, refreshUserToken, setError]
    );

    return {
        updateTicket,
        loading,
    };
};
